import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "emailInput", "form", "submitButton", "errorMessage"]
  static values = {
    subscribeUrl: String,
    likeUrl: String
  }

  connect() {
    this.subscriberEmail = localStorage.getItem("subscriber_email")
  }

  open(event) {
    event.preventDefault()

    if (this.subscriberEmail) {
      this.toggleLike()
      return
    }

    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    document.body.classList.add("overflow-hidden")

    setTimeout(() => {
      this.emailInputTarget.focus()
    }, 100)
  }

  close(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    document.body.classList.remove("overflow-hidden")
    this.clearError()
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) {
      this.close()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  async submit(event) {
    event.preventDefault()

    const email = this.emailInputTarget.value.trim()

    if (!this.validateEmail(email)) {
      this.showError("Veuillez entrer une adresse email valide")
      return
    }

    this.setLoading(true)
    this.clearError()

    try {
      const subscribeResponse = await fetch(this.subscribeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ email: email })
      })

      if (!subscribeResponse.ok) {
        const data = await subscribeResponse.json()
        throw new Error(data.error || "Erreur lors de l'inscription")
      }

      localStorage.setItem("subscriber_email", email)
      this.subscriberEmail = email

      await this.toggleLike()
      this.close()
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.setLoading(false)
    }
  }

  async toggleLike() {
    const response = await fetch(this.likeUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({ email: this.subscriberEmail })
    })

    if (response.ok) {
      const data = await response.json()
      this.updateUI(data.liked, data.likes_count)
    }
  }

  updateUI(liked, count) {
    const countEl = this.element.querySelector("[data-like-count]")
    if (countEl) countEl.textContent = count

    const button = this.element.querySelector("[data-action*='open']")
    const svg = button?.querySelector("svg")

    if (liked) {
      button.classList.remove("bg-gray-100", "text-gray-600", "hover:bg-red-50", "hover:text-red-500")
      button.classList.add("bg-red-100", "text-red-500")
      if (svg) svg.setAttribute("fill", "currentColor")
    } else {
      button.classList.remove("bg-red-100", "text-red-500")
      button.classList.add("bg-gray-100", "text-gray-600", "hover:bg-red-50", "hover:text-red-500")
      if (svg) svg.setAttribute("fill", "none")
    }
  }

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  setLoading(loading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
      this.submitButtonTarget.innerHTML = loading
        ? '<svg class="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>'
        : this.submitButtonTarget.dataset.originalText
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
